import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { get as _get } from 'lodash';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { ArrowBackSettings, IconNoStar, IconNoStaredMessage, UnStar } from '../../../assets/images';
import { IconUnstar } from '../../Common/Icons';
import './StarredMessages.scss';
import {
    downloadMediaFile,
    getActiveConversationChatId,
    getChatMessageHistoryById,
    isGroupChat,
    isSingleChat,
} from '../../../Helpers/Chat/ChatHelper';
import { formatDisplayName, isLocalUser } from '../../../Helpers/Chat/User';
import { getGroupData } from '../../../Helpers/Chat/Group';
import { callOriginStrMsgAction } from '../../../Actions/StarredAction';
import SDK from '../../SDK';
import { Store } from '../../../store';
import { ChatMessageHistoryDataAction, LoadMoreHistoryAction } from '../../../Actions/ChatHistory';
import { blockOfflineAction } from '../../../Helpers/Utility';
import MessageEntryList, { sentTimeOf } from '../../../features/chatDetail/ContactInfo/MessageEntryList';
import { ActiveChatAction } from '../../../Actions/RecentChatActions';
import { UnreadCountDelete } from '../../../Actions/UnreadCount';
import Button from '../../../ui/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../ui/Dropdown';
import { i18n } from '../../../localization/i18n';
import StarredRow from './StarredRow';

const StarredMessages = (props = {}) => {
    // Pointer-anchored menu, same approach as the chat list.
    const [contextMenu, setContextMenu] = useState({ open: false, x: 0, y: 0, message: null });
    const {
        handleBackToSetting,
        featureStateData: { isStarMessageEnabled = false } = {},
        handleShowCallScreen,
        inline = false,
    } = props;
    const { data: starData = [] } = useSelector(state => state.starredMessages);
    const chatConversation = useSelector(state => state.chatConversationHistory) || {};
    const { data: chatHistoreData = {} } = chatConversation;
    const [starredMessages, setStarredMessages] = useState([]);
    const [seletedData, setSeletedData] = useState({});
    const { starProfileData } = useSelector(state => state.rosterData);

    useEffect(() => {
        // Copy first: starData is the slice array, and sort would reorder it in place.
        const sortedData = [...starData].sort((x, y) => sentTimeOf(y) - sentTimeOf(x));
        const addReplyIsEsisting = sortedData.map(ele => {
            if (_get(ele, 'msgBody.replyTo', '') !== '') {
                return {
                    ...ele,
                    isReply: true,
                };
            } else {
                return {
                    ...ele,
                    isReply: false,
                };
            }
        });
        setStarredMessages(addReplyIsEsisting);
        return () => {
            setSeletedData({});
        };
    }, [starData, starProfileData]);

    useEffect(() => {
        if (Object.keys(chatHistoreData).length !== 0) {
            if (Object.keys(seletedData).length !== 0) {
                if (Object.keys(chatHistoreData[seletedData.fromUserId]?.messages)?.length !== 0) {
                    setTimeout(() => {
                        Store.dispatch(
                            callOriginStrMsgAction({
                                fromPage: 'starPage',
                                callOriginStrMsg: true,
                                callOriginStrMsgId: seletedData,
                            }),
                        );
                        setSeletedData({});
                    }, 500);
                }
            }
        }
    }, [chatHistoreData]);

    // publisherId is who wrote the message; fromUserId is the conversation it belongs to.
    const getSenderName = data => {
        const messageFrom = data?.publisherId;
        if (isLocalUser(messageFrom)) return i18n.t('chat.detail.YOU');

        const { nameToDisplay = '-' } = formatDisplayName(messageFrom) || {};
        return nameToDisplay;
    };

    // The group, or the other participant of a 1:1.
    // Never "You": a self-sent message would read "You · You".
    const getConversationName = data => {
        if (isGroupChat(data?.chatType)) {
            const { groupName = '' } = getGroupData(data?.fromUserId) || {};
            return groupName || '-';
        }
        if (isSingleChat(data?.chatType)) {
            const { nameToDisplay = '-' } = formatDisplayName(data?.fromUserId) || {};
            return nameToDisplay;
        }
        return '-';
    };

    const downloadAction = async (event, file_url, fileName, fileKey, msgId) => {
        downloadMediaFile(msgId, file_url, 'file', fileName, fileKey, event);
    };

    const unStarMessage = async data => {
        const storeData = Store.getState();
        const { recentChatData: { rosterData: { recentChatItems = [] } = {} } = {} } = storeData || {};
        const dataFind = recentChatItems.filter(ele => ele.recent.fromUserId === data.fromUserId);
        if (dataFind.length >= 1 && dataFind[0]?.roster?.groupId != null && dataFind[0]?.roster?.isAdminBlocked) {
            return;
        }
        if (blockOfflineAction()) return;
        SDK.updateFavouriteStatus(data.fromUserJid, [data.msgId], false);
    };

    // The global list spans every chat, so a row names its own.
    const renderMessageSource = message => `${getSenderName(message)} · ${getConversationName(message)}`;

    const handleRowContextMenu = (event, message) => {
        event.preventDefault();
        setContextMenu({ open: true, x: event.clientX, y: event.clientY, message });
    };

    const handleContextMenuOpenChange = isOpen => {
        setContextMenu(current => ({ ...current, open: isOpen }));
    };

    const scrollToMsg = async (msgId = '', fromUserId = '') => {
        setTimeout(async () => {
            const container = document?.getElementById(msgId);
            if (!container) return;
            container.scrollIntoView();
            container.classList.add('animatefinded');
            const activeConversationId = await getActiveConversationChatId();
            Store.dispatch(UnreadCountDelete({ activeConversationId }));
            const activeTimer = setTimeout(() => {
                container.classList.remove('animatefinded');
                Store.dispatch(
                    callOriginStrMsgAction({
                        fromPage: 'starPage',
                        callOriginStrMsg: false,
                        activeId: fromUserId,
                        msgId: msgId,
                    }),
                );
            }, 3000);
            return () => clearTimeout(activeTimer);
        }, 500);
    };

    const getMsgId = async (msgItem = {}, position = 'up') => {
        Store.dispatch(
            callOriginStrMsgAction({
                fromPage: 'starPage',
                callOriginStrMsg: true,
                activeId: msgItem.fromUserId,
                msgId: msgItem.msgId,
            }),
        );
        const storeData = Store.getState();
        const { recentChatData: { rosterData: { recentChatItems = [] } = {} } = {} } = storeData || {};
        const dataFind = recentChatItems.filter(ele => ele.recent.fromUserId === msgItem.fromUserId);

        if (dataFind.length >= 1) {
            dataFind[0].chatId = msgItem.fromUserId;
            dataFind[0].chatType = msgItem.chatType;
            dataFind[0].chatJid = msgItem.fromUserJid;

            if (dataFind[0]?.roster?.groupId != null && dataFind[0]?.roster?.isAdminBlocked) {
                toast.info('This group is no longer available');
                return;
            }

            const dataMsgHistory = getChatMessageHistoryById(msgItem.fromUserId);
            const lastMsgObject = dataMsgHistory?.[0];
            const lastMsgId = lastMsgObject?.msgId;
            const findListMagId = dataMsgHistory.find(ele => ele.msgId === msgItem.msgId);
            const activeChatId = getActiveConversationChatId();
            if (findListMagId && activeChatId === msgItem.fromUserId) {
                await scrollToMsg(findListMagId.msgId, msgItem.fromUserId);
            } else {
                Store.dispatch(LoadMoreHistoryAction(true));
                const { privacySettingsData } = Store.getState();
                const userSeenReceip = privacySettingsData.seenReceipt;
                const chatMessageRes = await SDK.getChatMessages({
                    toJid: msgItem.fromUserJid,
                    position: position,
                    lastMessageId: lastMsgId,
                    limit: 50,
                    includeMsgObj: true,
                    userSeenReceip,
                });
                if (chatMessageRes.statusCode === 200) {
                    Store.dispatch(ActiveChatAction(dataFind[0]));
                    Store.dispatch(ChatMessageHistoryDataAction(chatMessageRes));
                }
                const updatedDataMsgHistory = getChatMessageHistoryById(msgItem.fromUserId);
                const findListMagIdAfterFetch = updatedDataMsgHistory.find(ele => ele.msgId === msgItem.msgId);
                if (findListMagIdAfterFetch) {
                    setTimeout(async () => {
                        Store.dispatch(LoadMoreHistoryAction(false));
                        await scrollToMsg(findListMagIdAfterFetch.msgId, msgItem.fromUserId);
                    }, 500);
                } else {
                    await getMsgId(msgItem, 'up');
                }
            }
        }
    };

    return (
        <Fragment>
            <div id={'star-msgContent'} className={`staredMessages${inline ? ' inline' : ''}`}>
                <div>
                    <div className="contactlist">
                        {inline ? (
                            <StarredRow count={starredMessages.length} onBack={handleBackToSetting} />
                        ) : (
                            <div className="recent-chatlist-header">
                                <div className="sub-heading-container">
                                    <Button
                                        variant="ghost"
                                        onClick={handleBackToSetting}
                                        style={{
                                            height: '1.5rem',
                                            width: '1.5rem',
                                            padding: '0',
                                        }}
                                        className="newchat-icon"
                                        title="Back"
                                        onKeyDown={e => e.key === 'Enter' && handleBackToSetting}
                                        tabIndex="0"
                                    >
                                        <ArrowBackSettings />
                                    </Button>
                                    <span className="sub-heading">Starred Messages</span>
                                </div>
                            </div>
                        )}

                        {isStarMessageEnabled && starredMessages.length > 0 && (
                            <MessageEntryList
                                messages={starredMessages}
                                emptyText={i18n.t('chat.EMPTY_NO_DATA')}
                                actionIcon={IconUnstar}
                                actionLabel={i18n.t('message.ACTION_UNSTAR')}
                                onAction={unStarMessage}
                                onSelect={getMsgId}
                                renderWho={renderMessageSource}
                                onContextMenu={handleRowContextMenu}
                            />
                        )}
                        <DropdownMenu open={contextMenu.open} onOpenChange={handleContextMenuOpenChange}>
                            {/* Anchored to the pointer, so the trigger is a marker rather than a control. */}
                            <DropdownMenuTrigger
                                aria-hidden
                                tabIndex={-1}
                                className="starredContextAnchor"
                                style={{ left: contextMenu.x, top: contextMenu.y }}
                            />
                            <DropdownMenuContent align="start" sideOffset={0}>
                                <DropdownMenuItem
                                    title={i18n.t('message.ACTION_UNSTAR')}
                                    onSelect={() => contextMenu.message && unStarMessage(contextMenu.message)}
                                >
                                    <UnStar />
                                    <span>{i18n.t('message.ACTION_UNSTAR')}</span>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {starredMessages.length === 0 && (
                            <div className="box-center h-full w-full flex-auto noStarredMessage">
                                <i>
                                    <IconNoStaredMessage />
                                    <IconNoStar className="star" />
                                </i>
                                <p>{i18n.t('chat.EMPTY_NO_DATA')}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default StarredMessages;

StarredMessages.propTypes = {
    handleBackToSetting: PropTypes.func,
    handleShowCallScreen: PropTypes.func,
    featureStateData: PropTypes.object,
    inline: PropTypes.bool,
};
